import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { TokenType } from '@/data/_autogen/IValveVista';

let authStore: any = null;

/**
 * Initialize the auth interceptor with the auth store
 * This must be called after Pinia is initialized in main.ts
 */
export function setupAuthInterceptor(store: any) {
  authStore = store;

  // Request interceptor: Attach token to outgoing requests
  axios.interceptors.request.use(
    (config: any) => {
      if (!authStore) {
        return config;
      }

      const token = authStore.token;
      const tokenType = authStore.tokenType;

      // Only attach token to API requests (not other domains)
      const isApiRequest = config.url?.includes('/api/');

      if (token && isApiRequest) {
        if (tokenType === TokenType.GuestToken) {
          // Guest token uses custom header
          config.headers = config.headers || {};
          config.headers['X-Guest-Token'] = token;
        } else if (tokenType === TokenType.AccessToken) {
          // User token uses Authorization Bearer header
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle auth errors
  axios.interceptors.response.use(
    (response: any) => {
      return response;
    },
    (error: any) => {
      // Handle 401 Unauthorized responses
      if (error.response?.status === 401) {
        // Clear auth state when token is invalid
        authStore.clearAuth();

        // Optionally: Redirect to login page
        // router.push('/login')
      }

      // Handle 403 Forbidden responses
      if (error.response?.status === 403) {
        // Clear auth state when forbidden
        authStore.clearAuth();
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Get authorization header value
 * Used for manual header attachment if needed
 */
export function getAuthHeader(): { header: string; value: string } | null {
  if (!authStore || !authStore.token) {
    return null;
  }

  if (authStore.tokenType === TokenType.GuestToken) {
    return {
      header: 'X-Guest-Token',
      value: authStore.token,
    };
  }

  if (authStore.tokenType === TokenType.AccessToken) {
    return {
      header: 'Authorization',
      value: `Bearer ${authStore.token}`,
    };
  }

  return null;
}

/**
 * Check if current token is valid (not expired)
 */
export function isTokenValid(expiresInSeconds?: number): boolean {
  if (!authStore || !authStore.token || !expiresInSeconds) {
    return false;
  }

  // In real app would check against token expiration time stored in authStore
  // For now, just check if token exists
  return true;
}

/**
 * Check if request requires auth token
 */
export function requiresAuth(url: string): boolean {
  // Define which endpoints require authentication
  const authRequired = ['/api/v1/shoppingcart', '/api/v1/checkout', '/api/v1/auth/logout'];

  return authRequired.some((path) => url.includes(path));
}
